import { Box } from '@mui/system';
import {
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { format } from 'date-fns';
import { ChartSeriesEntry } from 'Portfolio';
import React from 'react';
import { Line } from 'react-chartjs-2';
import ExtendedLiquid from '../services/ExtendedLiquid';
import { Palette } from 'Palette';
import { getColorForLabel, StyledTypography } from './styles/styles';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

interface MultiIRRLineChartProps {
  data: { month: string; [label: string]: ChartSeriesEntry | string }[];
  seriesKeys: string[];
  tone: keyof typeof Palette;
}

export default function MultiIRRLineChart({ data, seriesKeys }: MultiIRRLineChartProps) {
  const labels = data.map((d) => format(new Date(`${d.month}-01`), 'MMM yy'));

  const chartData: ChartData<'line'> = {
    labels,
    datasets: seriesKeys.map((key, index) => {
      const seriesPoints = data.map((d) => d[key] as ChartSeriesEntry | undefined);

      return {
        label: key,
        data: seriesPoints.map((pt) => pt?.value ?? null),
        fill: false,
        borderWidth: 2,
        borderColor: getColorForLabel(index),
        tension: 0,
        pointRadius: 0,
        borderCapStyle: 'round',
        segment: {
          borderDash: (ctx: any) => {
            const currIndex = ctx.p0DataIndex;
            const curr = seriesPoints[currIndex];
            const next = seriesPoints[currIndex + 1];

            // Dotted if either side of the segment is forecast
            if (curr?.type === 'forecast' || next?.type === 'forecast') {
              return [1, 6];
            }
            return undefined; // solid
          },
        },
      };
    }),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const value = context.raw;
            const label = context.dataset.label || '';
            const formatted = ExtendedLiquid.currencyMillions(value, true, true, true, true); // 👈 your custom formatting
            return `${label}: ${formatted}`;
          },
        },
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      x: {
        ticks: { maxRotation: 45 },
      },
      y: {
        beginAtZero: false,
        ticks: {
          callback: (tickValue: string | number) => {
            const value = typeof tickValue === 'string' ? parseFloat(tickValue) : tickValue;
            return ExtendedLiquid.currencyMillions(value, value % 1000 === 0, true, true);
          },
        },
      },
    },
  };

  return (
    <>
      <div style={{ width: '100%', height: 500 }} className="react-MultiIRRLineChart">
        <Line data={chartData} options={options} />
      </div>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        gap={2}
        mt={1}
      >
        {seriesKeys.map((key, i) => (
          <Box key={key} display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 24,
                height: 4,
                backgroundColor: getColorForLabel(i),
              }}
            />
            <StyledTypography tone="tone1" component="span">
              {key}
            </StyledTypography>
          </Box>
        ))}
      </Box>
    </>
  );
}
